<template>
  <div
    id="dev-MapNavigation"
    class="select-card d-flex flex-column"
  >
    <!-- GPS Status Notification -->
    <transition name="gps-toast">
      <div
        v-if="gpsNotificationShow"
        class="gps-status-widget"
      >
        <div
          class="widget-content"
          :class="gpsStatusClass"
        >
          <div class="status-icon">
            <font-awesome-icon
              :icon="gpsStatusIcon"
              :class="{ spinning: gpsStatus === 'waiting' }"
            />
          </div>
          <div class="status-text">
            {{ gpsStatusMessage }}
          </div>
        </div>
      </div>
    </transition>

    <LocationPermissionModal
      id="dev-LocationPermissionModal"
      :show="showLocationModal"
      @allow="handleAllowLocation"
      @skip="handleSkipLocation"
    />

    <PermissionBlockedModal
      id="dev-PermissionBlockedModal"
      :show="showPermissionBlockedModal"
      @ok="handlePermissionBlockedOk"
    />

    <div class="map-navigation-container d-flex flex-row justify-content-between align-items-center w-100">
      <!-- Distance Confirmation Modal -->
      <DistanceConfirmationModal
        id="dev-DistanceConfirmationModal"
        :show="showDistanceConfirmationModal"
        :distance="distanceFromUser"
        @confirm="confirmDistanceSelection"
        @cancel="cancelDistanceSelection"
      />

      <!-- Active Rental Overlay / Content -->
      <div
        v-if="hasRentedBattery && !showMapOnly"
        class="active-rental-overlay"
      >
        <div class="active-rental-content">
          <!-- Success State -->
          <RentSuccess
            v-if="lastTransaction && lastTransaction.status === TAKE_BATTERY_SUCCESS"
            :last-transaction="lastTransaction"
            @show-map="handleShowMapFromSuccess"
          />

          <TransactionError
            v-else-if="
              lastTransaction &&
                [
                  'validation_error',
                  'take_battery_error',
                  'firmware_communication_error',
                  'timeouted',
                  'canceled_by_customer'
                ].includes(lastTransaction.status)
            "
            :last-transaction="lastTransaction"
          />
        </div>
      </div>

      <!-- Map Container -->
      <div
        ref="mapContainer"
        class="map-container"
      ></div>

      <!-- Back Button  -->
      <BaseButton
        v-if="showMapOnly"
        bare
        variant=""
        class="map-back-btn"
        :aria-label="$t('map_navigation.back') || 'Voltar'"
        @click="showCloseButton ? $emit('close') : (showMapOnly = false)"
      >
        <font-awesome-icon icon="fa-solid fa-arrow-left" />
        <span class="back-text">{{ $t('map_navigation.back') }}</span>
      </BaseButton>

      <!-- Glassmorphism Manual Token Input Pill -->
      <transition name="pill-entrance">
        <BaseButton
          v-if="!loading && !hideManualInput && (isManualInputMinimized || !showManualTokenInput)"
          bare
          variant=""
          class="manual-input-pill"
          :aria-label="$t('map_navigation.show_token_input')"
          @click="handleManualInputPillClick"
        >
          <font-awesome-icon
            icon="fa-solid fa-keyboard"
            class="pill-icon"
          />
          <span class="pill-text">{{ $t('map_navigation.manual_token_input') }}</span>
        </BaseButton>
      </transition>

      <!-- Map Overlay with Manual Token Input -->
      <div
        v-if="showManualTokenInput && !isManualInputMinimized"
        class="map-overlay-backdrop"
        @click="minimizeManualInput"
      >
        <!-- Location Retry Button -->
        <div v-if="!hasLocationPermission">
          <BaseButton
            bare
            variant=""
            class="location-retry-floating-btn"
            :aria-label="$t('map_navigation.retry_location')"
            @click.stop="retryGeolocation"
          >
            <font-awesome-icon
              icon="fa-solid fa-location-crosshairs"
              class="location-icon"
            />
            <span class="retry-text">{{ $t('map_navigation.retry_location') }}</span>
          </BaseButton>
        </div>

        <!-- Manual Token Input Dialog -->
        <ManualTokenInputDialog
          id="dev-ManualTokenInputDialog"
          ref="manualTokenInputDialogRef"
          :show="true"
          :mode="mode"
          :token="manualToken"
          :selected-point="manualSelectedPoint"
          :error="tokenInputError"
          :is-validating="isValidating"
          :user-location="userLocation"
          :has-location-permission="hasLocationPermission"
          @update:token="onManualTokenInput"
          @minimize="minimizeManualInput"
          @select-station="handleSelectManualStation"
          @check-prices="handleCheckManualPrices"
          @click.stop
        />
      </div>

      <!-- Point Details Dialog -->
      <PointDetailsDialog
        id="dev-PointDetailsDialog"
        ref="pointDetailsDialogRef"
        :mode="mode"
        :point="selectedPoint"
        :user-location="userLocation"
        :has-location-permission="hasLocationPermission"
        :user="user"
        @close="closeDialog"
        @check-prices="handleCheckPrices"
        @start-checkout="handleStartCheckout"
        @token-changed="handleDialogTokenChanged"
      />

      <!-- Checkout Flow Modal -->
      <CheckoutFlowModal
        ref="checkoutFlowModalRef"
        :is-open="showCheckoutModal"
        :point="checkoutPoint"
        :user="user"
        :pending-pix="pendingPixData"
        :initial-cable-type="cableType"
        @close="closeCheckoutModal"
        @start-rental="handleStartRental"
        @cable-changed="handleCableChanged"
        @validate-transaction="handleValidateTransaction"
        @error="showError"
        @pix-payment-authorized="$emit('pix-payment-authorized', $event)"
      />

      <!-- Validation Error Dialog -->
      <ValidationErrorDialog
        v-if="showValidationErrorDialog && validationError"
        :error-data="validationError"
        :nearby-terminals="nearbyTerminalsForError"
        :is-validating="isValidating"
        :user="user"
        @close="handleValidationErrorClose"
        @change-cable="handleChangeCableType"
        @retry="handleRetryValidation"
        @select-terminal="handleSelectNearbyTerminal"
        @select-cable="handleSelectCable"
        @secondary-action="handleSecondaryErrorAction"
      />

      <!-- Loading Overlay -->
      <LoadingOverlay :show="loading" />

      <!-- Refresh Points Button -->
      <button
        class="refresh-points-btn"
        :aria-label="$t('map_navigation.refresh_points') || 'Atualizar pontos'"
        @click="fetchPoints"
      >
        <font-awesome-icon
          :icon="isRefreshing ? 'fa-solid fa-spinner' : 'fa-solid fa-arrows-rotate'"
          :class="{ spinning: isRefreshing }"
          class="refresh-icon"
        />
      </button>

      <!-- Locate Me Button -->
      <button
        v-if="!isQRFlow"
        class="locate-me-btn"
        :class="[`state-${locateMeState}`, { 'is-centered': isMapCentered, 'flash-acquired': gpsAcquiredFlash }]"
        :aria-label="locateMeAriaLabel"
        @click="centerOnUserLocation"
      >
        <span
          v-if="locateMeState === 'locating'"
          class="locate-spinner"
        ></span>
        <font-awesome-icon
          v-else
          :icon="locateMeIcon"
          class="locate-icon"
        />
      </button>
    </div>
  </div>
</template>

<script>
  import L from 'leaflet'
  import 'leaflet/dist/leaflet.css'
  import markerclustergroup from 'leaflet.markercluster'
  import 'leaflet.markercluster/dist/MarkerCluster.css'
  import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
  import API from '@/services/api'
  import getPointByToken from '@/services/getPointName'
  import { sessionRecorder, UserEvent, SESSION_EVENTS } from '@/services/sessionRecorder'

  import LocationPermissionModal from './MapNavigation/modals/LocationPermissionModal.vue'
  import PermissionBlockedModal from './MapNavigation/modals/PermissionBlockedModal.vue'
  import DistanceConfirmationModal from './MapNavigation/modals/DistanceConfirmationModal.vue'
  import CheckoutFlowModal from './MapNavigation/modals/CheckoutFlowModal.vue'
  import ValidationErrorDialog from './MapNavigation/dialogs/ValidationErrorDialog.vue'
  import PointDetailsDialog from './MapNavigation/dialogs/PointDetailsDialog.vue'
  import ManualTokenInputDialog from './MapNavigation/dialogs/ManualTokenInputDialog.vue'
  import LoadingOverlay from './MapNavigation/ui/LoadingOverlay.vue'
  import BaseButton from '@/components/ui/BaseButton.vue'
  import TransactionError from '@/components/TransactionError.vue'
  import RentSuccess from '@/components/RentSuccess.vue'

  import {
    calculateDistance,
    calculateMapCenterFromPoints,
    checkGeolocationPermission,
    requestGeolocationPermission,
    getMapLocation,
    isPointOffline,
    startGPSAcquisition,
    startGPSWatch
  } from './MapNavigation/utils/mapHelpers'
  import {
    DEFAULT_CENTER,
    MAX_DISTANCE_KM,
    MAX_GEOLOCATION_ATTEMPTS,
    LOCATION_CACHE_TIMEOUT,
    MARKER_COLOR_AVAILABLE,
    MARKER_COLOR_UNAVAILABLE,
    OFFLINE_TERMINAL_ICON
  } from './MapNavigation/utils/constants'
  import { getNearbyPointsWithBatteries, ERROR_TYPES, getErrorType } from './MapNavigation/utils/errorHandlers'
  import { TAKE_BATTERY_SUCCESS } from '@/constants.js'

  // Fix Leaflet default icon paths
  delete L.Icon.Default.prototype._getIconUrl
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png'
  })

  export default {
    name: 'MapNavigation',
    components: {
      LocationPermissionModal,
      PermissionBlockedModal,
      DistanceConfirmationModal,
      CheckoutFlowModal,
      ValidationErrorDialog,
      PointDetailsDialog,
      ManualTokenInputDialog,
      LoadingOverlay,
      BaseButton,
      TransactionError,
      RentSuccess
    },
    props: {
      mode: {
        type: String,
        default: 'withdrawal',
        validator: value => ['withdrawal', 'return'].includes(value)
      },
      hasRentedBattery: {
        type: Boolean,
        default: false
      },
      showCloseButton: {
        type: Boolean,
        default: false
      },
      initialToken: {
        type: String,
        default: null
      },
      cableType: {
        type: Number,
        default: null
      },
      user: {
        type: Object,
        default: () => ({})
      },
      lastTransaction: {
        type: Object,
        default: null
      },
      campaign: {
        type: Object,
        default: null
      },
      startMapOnly: {
        type: Boolean,
        default: false
      },
      hideManualInput: {
        type: Boolean,
        default: false
      }
    },
    emits: [
      'close',
      'withdraw-battery',
      'start-rental',
      'check-prices',
      'change-cable-type',
      'start-manual-checkout',
      'modal-state-change',
      'update-location',
      'pix-payment-authorized'
    ],
    data() {
      return {
        TAKE_BATTERY_SUCCESS,
        map: null,
        loading: true,
        loadingError: false,
        userLocation: null,
        gpsLocation: null,
        gpsStatus: 'waiting',
        hasLocationPermission: false,
        userMarker: null,
        isMapCentered: false,
        cancelGPSWatch: null,
        points: [],
        selectedPoint: null,
        markers: [],
        markerClusterGroup: null,
        showLocationModal: false,
        locationModalShown: false,
        showPermissionBlockedModal: false,
        showManualTokenInput: false,
        manualToken: '',
        manualSelectedPoint: null,
        tokenInputError: '',
        geolocationAttempts: 0,
        maxGeolocationAttempts: MAX_GEOLOCATION_ATTEMPTS,
        isManualInputMinimized: true,
        showDistanceConfirmationModal: false,
        pendingWithdrawalData: null,
        MAX_DISTANCE_KM: MAX_DISTANCE_KM,
        detailsOpenedAt: null,
        showValidationErrorDialog: false,
        validationError: null,
        isValidating: false,
        showCheckoutModal: false,
        checkoutPoint: null,
        pendingPixData: null,
        showNearestHand: true,
        showMapOnly: this.startMapOnly,
        isRefreshing: false,
        isQRFlow: false,

        hideAcquired: false,
        acquiredTimer: null,
        gpsAcquiredFlash: false,
        showGpsWaiting: false,
        gpsWaitingTimer: null,
        hasRealGPSSignal: false
      }
    },
    computed: {
      isReturnMode() {
        return this.mode === 'return'
      },

      isLoggedIn() {
        return !!localStorage.getItem('token')
      },

      filteredPoints() {
        // We want to show all points on the map, regardless of mode.
        // Markers will be colored green/red based on availability within displayMarkers.
        return this.points
      },

      actionButtonText() {
        return this.isReturnMode ? this.$t('map_navigation.return_battery') : this.$t('map_navigation.select_terminal')
      },

      distanceFromUser() {
        if (!this.selectedPoint || !this.userLocation || !this.hasLocationPermission) {
          return null
        }
        const distance = calculateDistance(
          this.userLocation.lat,
          this.userLocation.lng,
          parseFloat(this.selectedPoint.geolocation.lat),
          parseFloat(this.selectedPoint.geolocation.lng)
        )
        return distance.toFixed(1)
      },

      nearestPoint() {
        if (!this.userLocation || !this.hasLocationPermission || this.filteredPoints.length === 0) {
          return null
        }

        // Only consider terminals that actually have availability AND are online.
        // Offline check is the precedent: it must be evaluated before availability
        // because a terminal may report `is_online: false` with stale stock values.
        const availablePoints = this.filteredPoints.filter(point => {
          if (isPointOffline(point)) return false

          const slots = parseInt(point.available_slots || point.avaible_slots || 0)
          const batteries = parseInt(point.available_batteries || point.avaible_batteries || 0)
          return this.isReturnMode ? slots > 0 : batteries > 0
        })

        if (availablePoints.length === 0) {
          return null
        }

        let nearest = null
        let minDistance = Infinity

        availablePoints.forEach(point => {
          if (!point.geolocation || !point.geolocation.lat || !point.geolocation.lng) return

          const distance = calculateDistance(
            this.userLocation.lat,
            this.userLocation.lng,
            parseFloat(point.geolocation.lat),
            parseFloat(point.geolocation.lng)
          )
          if (distance < minDistance) {
            minDistance = distance
            nearest = point
          }
        })

        return nearest
      },

      isAnyModalOpen() {
        return (
          this.showLocationModal ||
          this.showPermissionBlockedModal ||
          this.showDistanceConfirmationModal ||
          this.showCheckoutModal ||
          this.showValidationErrorDialog ||
          (this.showManualTokenInput && !this.isManualInputMinimized)
        )
      },

      nearbyTerminalsForError() {
        // Only calculate if we have a validation error with terminal info
        if (!this.validationError?.extra?.terminal) {
          return []
        }

        const currentTerminal = this.validationError.extra.terminal

        // Get nearby terminals within 300m (0.3km)
        return getNearbyPointsWithBatteries(this.points, currentTerminal, 0.3, 3)
      },

      gpsNotificationShow() {
        if (this.isQRFlow) return false
        if (this.gpsStatus === 'waiting') return this.showGpsWaiting
        return false
      },
      gpsStatusClass() {
        return `status-${this.gpsStatus}`
      },
      gpsStatusIcon() {
        switch (this.gpsStatus) {
          case 'waiting':
            return 'fa-solid fa-spinner'
          case 'acquired':
            return 'fa-solid fa-circle-check'
          default:
            return 'fa-solid fa-spinner'
        }
      },
      gpsStatusMessage() {
        switch (this.gpsStatus) {
          case 'waiting':
            return this.$t('map_navigation.waiting_gps_signal') || '🔄 Aguardando sinal de GPS...'
          case 'acquired':
            return this.$t('map_navigation.gps_acquired') || '✓ Localização encontrada'
          default:
            return ''
        }
      },

      locateMeState() {
        if (this.gpsStatus === 'waiting') return 'locating'
        if (this.gpsStatus === 'failed') return 'denied'
        if (this.isMapCentered) return 'centered'
        return 'idle'
      },
      locateMeIcon() {
        return 'fa-solid fa-location-crosshairs'
      },
      locateMeAriaLabel() {
        switch (this.locateMeState) {
          case 'locating':
            return this.$t('map_navigation.locating_gps')
          case 'denied':
            return this.$t('map_navigation.enable_location')
          case 'centered':
            return this.$t('map_navigation.my_location')
          default:
            return this.$t('map_navigation.center_on_me')
        }
      }
    },
    watch: {
      mode() {
        // Redesenha os marcadores quando o modo muda (ex: de retirada para devolução)
        this.displayMarkers()
      },

      hasRentedBattery(newValue) {
        if (newValue) {
          this.showMapOnly = false
        }
      },

      isAnyModalOpen(newValue) {
        this.$emit('modal-state-change', newValue)
      },

      userLocation: {
        handler() {
          // Use updateHandVisibility instead of displayMarkers to avoid resetting
          // expanded cluster state on every GPS position update.
          // Same rationale as the zoomend handler above.
          this.updateHandVisibility()
        },
        deep: true
      },

      nearestPoint(newPoint, oldPoint) {
        // Rebuild markers only when the nearest point changes identity (e.g. initial
        // GPS fix, or user moves far enough for a different terminal to become nearest).
        // This updates the "nearest hand" icon on the correct marker without triggering
        // a full clearLayers() on every GPS drift update.
        if ((newPoint?.point_id ?? null) !== (oldPoint?.point_id ?? null)) {
          this.displayMarkers()
        }
      },

      filteredPoints: {
        handler() {
          this.displayMarkers()
        },
        deep: true
      },

      selectedPoint(newPoint, oldPoint) {
        // Track when point details are opened
        if (newPoint && !oldPoint) {
          this.detailsOpenedAt = Date.now()
          this.trackEvent(
            SESSION_EVENTS.MAP_POINT_DETAILS_OPENED,
            this.getPointExtras(newPoint, {
              has_availability: this.isReturnMode
                ? parseInt(newPoint.available_slots) > 0
                : parseInt(newPoint.available_batteries) > 0
            })
          )
        }
      },

      cableType(newCableType, oldCableType) {
        // Just log the cable type change for debugging
        // The handleSelectCable method handles retry logic properly
        if (newCableType !== oldCableType && newCableType !== null && newCableType !== undefined) {
          console.log('[MapNavigation] Cable type changed from', oldCableType, 'to', newCableType)
        }
      },

      gpsStatus(newStatus) {
        // If GPS acquisition fails after map was initialized without showing manual input,
        // show it now so the user has an alternative way to find terminals
        if (newStatus === 'failed' && !this.showManualTokenInput && this.map) {
          this.openManualTokenInput()
        }

        // Show "Buscando sinal de GPS..." only after a delay, so quick acquisitions
        // don't flash the notification at all
        clearTimeout(this.gpsWaitingTimer)
        if (newStatus === 'waiting') {
          this.showGpsWaiting = false
          this.gpsWaitingTimer = setTimeout(() => {
            if (this.gpsStatus === 'waiting') {
              this.showGpsWaiting = true
            }
          }, 5000)
        } else {
          this.showGpsWaiting = false
        }

        // Flash the locate-me button briefly when GPS is acquired
        if (newStatus === 'acquired') {
          this.gpsAcquiredFlash = true
          clearTimeout(this.acquiredTimer)
          this.acquiredTimer = setTimeout(() => {
            this.gpsAcquiredFlash = false
          }, 2000)
        }
      }
    },
    async mounted() {
      // If there's a pending PIX checkout, restore it immediately without loading the map
      if (this._restorePixCheckoutFromSession()) return

      // Check if user came from QR code scan via prop or sessionStorage
      const qrToken = this.initialToken || sessionStorage.getItem('qr_token')

      if (qrToken) {
        this.isQRFlow = true
        // User came from QR code - use token location instead of asking for geolocation
        await this.initializeMapWithQRToken(qrToken)
      } else {
        this.isQRFlow = false
        // Normal flow - ask for geolocation permission
        await this.initializeMap()
      }
    },
    beforeUnmount() {
      clearTimeout(this.acquiredTimer)
      clearTimeout(this.gpsWaitingTimer)
      if (this.cancelGPSWatch) {
        this.cancelGPSWatch()
        this.cancelGPSWatch = null
      }
      if (this.map) {
        if (this.markerClusterGroup) {
          this.markerClusterGroup.clearLayers()
        }
        this.map.remove()
      }
    },
    methods: {
      handleShowMapFromSuccess() {
        this.showMapOnly = true
        this.showManualTokenInput = false
        this.isManualInputMinimized = false
      },

      _restorePixCheckoutFromSession() {
        try {
          const stored = localStorage.getItem('pix_checkout')
          if (!stored) return false
          const data = JSON.parse(stored)
          const elapsedMinutes = (Date.now() - data.pixCreatedAt) / 60000
          if (elapsedMinutes >= 1440) {
            localStorage.removeItem('pix_checkout')
            return false
          }
          this.pendingPixData = data
          this.checkoutPoint = { token: data.token, name: data.pointName, point_id: data.pointId }
          this.showCheckoutModal = true
          return true
        } catch {
          localStorage.removeItem('pix_checkout')
          return false
        }
      },

      trackEvent(eventName, extras = {}) {
        try {
          const transactionId = this.pendingWithdrawalData?.transactionId || null
          sessionRecorder.addUserEvent(
            new UserEvent(eventName, transactionId, {
              mode: this.mode,
              ...extras
            })
          )
        } catch (error) {
          console.warn(`Failed to track ${eventName}:`, error)
        }
      },

      getPointExtras(point, additionalExtras = {}) {
        if (!point) return { ...additionalExtras }

        return {
          point_id: point.point_id,
          point_name: point.name,
          available_batteries: point.available_batteries,
          available_slots: point.available_slots,
          distance_km: this.calculateDistanceToPoint(point),
          token: point.token,
          ...additionalExtras
        }
      },

      calculateDistanceToPoint(point) {
        if (!point || !this.userLocation || !this.hasLocationPermission) {
          return null
        }
        try {
          const distance = calculateDistance(
            this.userLocation.lat,
            this.userLocation.lng,
            parseFloat(point.geolocation.lat),
            parseFloat(point.geolocation.lng)
          )
          return distance.toFixed(1)
        } catch (error) {
          return null
        }
      },

      getCachedLocation() {
        try {
          const cached = localStorage.getItem('user_location_cache')
          if (!cached) return null

          const { lat, lng, timestamp } = JSON.parse(cached)
          const now = Date.now()

          if (now - timestamp < LOCATION_CACHE_TIMEOUT) {
            return { lat, lng }
          }

          // Cache expired
          localStorage.removeItem('user_location_cache')
          return null
        } catch (error) {
          console.warn('[MapNavigation] Error reading location cache:', error)
          return null
        }
      },

      setCachedLocation(location) {
        if (!location || !location.lat || !location.lng) return

        try {
          const cacheData = {
            lat: location.lat,
            lng: location.lng,
            timestamp: Date.now()
          }
          localStorage.setItem('user_location_cache', JSON.stringify(cacheData))
        } catch (error) {
          console.warn('[MapNavigation] Error saving location cache:', error)
        }
      },

      async initializeMapWithQRToken(qrToken) {
        try {
          console.log('[MapNavigation] Initializing with QR token:', qrToken)
          this.loadingError = false

          await this.fetchPoints()

          console.log('[MapNavigation] Total points loaded:', this.points.length)

          // Sempre buscar dados frescos da API em vez de depender do cache local
          const pointData = await getPointByToken(qrToken)

          if (!pointData || !pointData.id) {
            console.warn('[MapNavigation] Could not find point for QR token, falling back to normal flow')

            this.trackEvent(SESSION_EVENTS.MAP_QR_INITIALIZATION_FAILED, {
              token: qrToken,
              error_reason: 'point_not_found'
            })

            await this.initializeMap()
            return
          }

          const tokenPoint = {
            point_id: pointData.id,
            name: pointData.name,
            address: pointData.address || this.$t('map_navigation.address_not_available'),
            available_batteries: pointData.available_batteries || 0,
            available_slots: pointData.available_slots || 0,
            geolocation: pointData.geolocation,
            token: qrToken
          }

          if (tokenPoint && tokenPoint.geolocation) {
            this.userLocation = {
              lat: parseFloat(tokenPoint.geolocation.lat),
              lng: parseFloat(tokenPoint.geolocation.lng)
            }

            this.hasLocationPermission = true
            this.gpsStatus = 'acquired' // Marcar como adquirido imediatamente no fluxo de QR

            console.log('[MapNavigation] Using token location as user location:', this.userLocation)
            console.log('[MapNavigation] hasLocationPermission set to:', this.hasLocationPermission)

            sessionStorage.removeItem('qr_token')

            this.trackEvent(SESSION_EVENTS.MAP_QR_INITIALIZATION_SUCCESS, {
              token: qrToken,
              point_id: tokenPoint.point_id,
              point_name: tokenPoint.name
            })
            this.isQRFlow = true
          } else {
            console.warn('[MapNavigation] Could not find geolocation for QR token, falling back to normal flow')

            this.trackEvent(SESSION_EVENTS.MAP_QR_INITIALIZATION_FAILED, {
              token: qrToken,
              error_reason: 'no_geolocation'
            })

            this.isQRFlow = false
            await this.initializeMap()
            return
          }

          await this.initializeMapContent()

          // Abre automaticamente os detalhes do ponto (simulando o clique)
          // Isso garante que a tela PointDetailsDialog apareça imediatamente
          if (tokenPoint) {
            this.onMarkerClick(tokenPoint)
          }

          // No fluxo de QR, não precisamos de GPS real em background pois assumimos
          // que o usuário está no terminal. Isso evita delays e pedidos de permissão.
          /* 
        checkGeolocationPermission().then(hasPermission => {
          if (hasPermission) {
            this.startBackgroundGPSAcquisition(false)
          } else {
            // If no permission, we're already using terminal location, so we can consider it "failed" or just leave it
            // For QR flow, we'll set it to 'failed' so the "Waiting" toast disappears
            this.gpsStatus = 'failed'
          }
        })
        */
        } catch (error) {
          this.loading = false
          this.loadingError = true
          console.error('Error initializing map with QR token:', error)

          this.trackEvent(SESSION_EVENTS.MAP_QR_INITIALIZATION_FAILED, {
            token: qrToken,
            error_reason: error.message
          })
        }
      },

      async initializeMap() {
        try {
          this.loadingError = false

          if (this.points.length === 0) {
            await this.fetchPoints()
          }

          const cachedLocation = this.getCachedLocation()

          if (cachedLocation) {
            console.log('[MapNavigation] Using cached location:', cachedLocation)
            this.userLocation = cachedLocation
            this.gpsLocation = cachedLocation
            this.gpsStatus = 'acquired'
            this.hasLocationPermission = true

            // Initialize map with cached location
            await this.initializeMapContent()

            // Still start background watch to keep it updated, but don't pan
            this.startBackgroundGPSAcquisition(false)
            return
          }

          this.gpsStatus = 'waiting'

          this.userLocation = calculateMapCenterFromPoints(this.points, this.mode)
          if (!this.userLocation || !this.userLocation.lat) {
            this.userLocation = DEFAULT_CENTER
          }

          // Initialize map immediately with default center
          await this.initializeMapContent()

          const hasPermission = await checkGeolocationPermission()

          if (hasPermission) {
            this.startBackgroundGPSAcquisition()
          } else {
            // Check if it's permanently denied or just not granted yet
            const permissionStatus = await requestGeolocationPermission()

            if (permissionStatus.blocked) {
              // Permission is permanently blocked
              this.gpsStatus = 'failed'
              this.showPermissionBlockedModal = true
            } else if (!permissionStatus.granted) {
              // Permission not granted on first attempt, show modal
              if (!this.locationModalShown) {
                this.showLocationModal = true
                this.locationModalShown = true
              }
            } else {
              // Permission was just granted, start GPS acquisition
              this.startBackgroundGPSAcquisition()
            }
          }
        } catch (error) {
          this.gpsStatus = 'failed'
          console.error('Error initializing map:', error)

          this.trackEvent(SESSION_EVENTS.MAP_INITIALIZATION_FAILED, {
            error_message: error.message
          })
        }
      },

      async attemptGetLocation() {
        try {
          this.userLocation = await getMapLocation()
          this.gpsLocation = { ...this.userLocation }
          this.gpsStatus = 'acquired'
          this.hasLocationPermission = true
          this.hasRealGPSSignal = true
        } catch (error) {
          console.warn('Location access not available, using default center:', error.message)
          this.hasLocationPermission = false
          this.gpsStatus = 'failed'

          await this.fetchPoints()
          this.userLocation = calculateMapCenterFromPoints(this.points, this.mode)

          if (!this.userLocation || !this.userLocation.lat) {
            this.userLocation = DEFAULT_CENTER
          }
        }

        if (this.points.length === 0) {
          await this.fetchPoints()
        }
        await this.initializeMapContent()
      },

      async startBackgroundGPSAcquisition(shouldPan = true) {
        // Start GPS watch (continuous updates) in background without blocking map rendering
        try {
          if (this.cancelGPSWatch) this.cancelGPSWatch()
          const { promise, cancel } = startGPSWatch(updatedLocation => {
            // Called on every subsequent GPS update after first fix
            this.gpsLocation = { ...updatedLocation }
            this.userLocation = updatedLocation
            this.updateUserMarker(updatedLocation)
            this.setCachedLocation(updatedLocation)
            this.$emit('update-location', updatedLocation)
          })

          // Save cancel function for cleanup on component unmount
          this.cancelGPSWatch = cancel

          const location = await promise

          if (location) {
            this.gpsLocation = { ...location }
            this.gpsStatus = 'acquired'
            this.hasLocationPermission = true
            this.hasRealGPSSignal = true
            this.userLocation = location
            this.setCachedLocation(location)
            this.$emit('update-location', location)

            // Update map center to GPS location only if shouldPan is true
            if (shouldPan && this.map && this.map.setView) {
              this.map.setView([location.lat, location.lng], 15)
              this.isMapCentered = true
            }

            // Place / move the blue dot on the real GPS position
            this.updateUserMarker(location)

            console.log('[MapNavigation] GPS acquired:', location)
          }
        } catch (error) {
          const isPermissionDenied = error.code === 1 || error.message?.includes('permission')
          this.gpsStatus = 'failed'
          this.hasLocationPermission = false
          this.cancelGPSWatch = null
          if (isPermissionDenied) {
            console.warn('[MapNavigation] GPS permission denied')
          } else {
            console.warn('[MapNavigation] GPS acquisition failed:', error)
          }
        }
      },

      /**
       * Create or update the blue dot on the map at the given location.
       * Always keeps a single marker instance in this.userMarker.
       */
      updateUserMarker(location) {
        if (!this.map || !location || !location.lat || !location.lng) return

        try {
          if (this.userMarker) {
            // Move the existing marker instead of recreating it
            this.userMarker.setLatLng([location.lat, location.lng])
          } else {
            const userIcon = L.divIcon({
              className: 'user-location-marker',
              html: '<div class="user-marker-pulse"></div>',
              iconSize: [20, 20],
              iconAnchor: [10, 10]
            })
            this.userMarker = L.marker([location.lat, location.lng], {
              icon: userIcon,
              title: this.$t('map.my_location'),
              zIndexOffset: 1000
            }).addTo(this.map)
          }
          console.log('[MapNavigation] User marker placed at:', location)
        } catch (error) {
          console.error('[MapNavigation] Error updating user location marker:', error)
        }
      },

      /**
       * Center the map on the user's GPS location.
       * If GPS not yet acquired, start acquisition.
       * If GPS denied, show permission modal.
       */
      async centerOnUserLocation() {
        if (this.gpsStatus === 'acquired' && this.gpsLocation) {
          this.map.setView([this.gpsLocation.lat, this.gpsLocation.lng], 16, { animate: true })
          this.isMapCentered = true
          this.gpsStatus = 'waiting'
          // Force refresh in background
          this.startBackgroundGPSAcquisition(false)
          return
        }

        if (this.gpsStatus === 'waiting') {
          // Still acquiring — just wait; the toast already indicates that
          return
        }

        // GPS failed or not started — request permission and try again
        if (this.gpsStatus === 'failed') {
          const permissionResult = await requestGeolocationPermission()
          if (permissionResult.blocked) {
            this.showPermissionBlockedModal = true
            return
          }
          if (permissionResult.granted) {
            this.gpsStatus = 'waiting'
            this.startBackgroundGPSAcquisition()
          } else {
            this.showLocationModal = true
          }
        }
      },

      async fetchPoints() {
        if (this.isRefreshing) return
        this.isRefreshing = true
        try {
          const response = await API.get('points')
          this.points = response.data || []
          this.loadingError = false
          this.displayMarkers()
        } catch (error) {
          // Silently handle 401/403 for public map view
          if (error.response?.status === 401 || error.response?.status === 403) {
            console.warn('[MapNavigation] Unauthorized access to points, trying public access if available')
          } else {
            console.error('Error fetching points:', error)
          }
          this.points = []
          this.loadingError = true
        } finally {
          this.isRefreshing = false
        }
      },

      displayMarkers() {
        if (!this.markerClusterGroup) {
          return
        }

        this.markerClusterGroup.clearLayers()
        this.markers = []

        const nearest = this.nearestPoint

        this.filteredPoints.forEach(point => {
          // Show hand if it's the nearest AND no point is currently selected AND showNearestHand is true
          // The hand will appear on the marker if not clustered, or on the cluster if clustered
          const isNearest =
            this.showNearestHand && nearest && point.point_id === nearest.point_id && !this.selectedPoint

          const slotsCount = parseInt(point.available_slots || point.avaible_slots || 0)
          const batteriesCount = parseInt(point.available_batteries || point.avaible_batteries || 0)

          const availableCount = this.isReturnMode ? slotsCount : batteriesCount

          // Use the same robust offline detection everywhere so the marker
          // and the dialog stay in sync, even when the API returns
          // non-boolean values for `is_online`.
          const isOffline = isPointOffline(point)
          const iconHtml = this.createMarkerIcon(availableCount, isNearest, isOffline)
          const customIcon = L.divIcon({
            className: 'custom-marker',
            html: iconHtml,
            iconSize: [40, 50],
            iconAnchor: [20, 50],
            popupAnchor: [0, -50]
          })

          const marker = L.marker([parseFloat(point.geolocation.lat), parseFloat(point.geolocation.lng)], {
            icon: customIcon
          })

          marker.on('click', () => {
            this.onMarkerClick(point)
          })

          this.markerClusterGroup.addLayer(marker)
          this.markers.push(marker)
        })
      },

      updateHandVisibility() {
        // Update hand visibility on ALL markers and clusters without reconstructing them
        // This prevents closing expanded clusters
        const handElements = document.querySelectorAll('.nearest-hand-indicator')
        handElements.forEach(el => {
          if (this.showNearestHand) {
            el.style.display = 'flex'
            el.style.visibility = 'visible'
          } else {
            el.style.display = 'none'
            el.style.visibility = 'hidden'
          }
        })
      },

      hideClusterHandOnly() {
        // Hide only the cluster hand, not the marker hands
        const clusterHands = document.querySelectorAll('[data-cluster-hand="true"]')
        clusterHands.forEach(el => {
          el.style.display = 'none'
          el.style.visibility = 'hidden'
        })
      },

      createMarkerIcon(count, isNearest = false, isOffline = false) {
        // Precedence rules:
        //   1. offline  → red marker + plug icon, NEVER show a count (even 0)
        //   2. available (count > 0) → green marker + count
        //   3. out of stock → red marker + count = "0"
        const isAvailable = !isOffline && count > 0
        const backgroundColor = isAvailable ? MARKER_COLOR_AVAILABLE : MARKER_COLOR_UNAVAILABLE
        const nearestClass = isNearest ? 'is-nearest' : ''
        const handText = this.isReturnMode ? this.$t('map_navigation.return_here') : this.$t('map_navigation.rent_here')
        const handHtml = isNearest
          ? `
        <div class="nearest-hand-indicator">
          <div class="rent-here-balloon">${handText}</div>
        </div>
      `
          : ''

        // Battery count is rendered only when the terminal is online. Offline
        // markers must never show a number (not even "0") to avoid misleading
        // the user into thinking the terminal simply has no stock.
        const countText = isOffline ? '' : String(count)

        // The marker is built as a raw HTML string (consumed by Leaflet's
        // divIcon), so we cannot use the <font-awesome-icon> component here.
        // The inline SVG below matches the `fa-plug` glyph that is already
        // registered in main.js and used elsewhere in the project.
        // Fill is set inline (#dc3545) so the icon stays visible on the
        // white availability-circle background.
        const plugSvg = isOffline
          ? `<svg viewBox="0 0 384 512" width="15" height="15" style="fill: ${MARKER_COLOR_UNAVAILABLE}; display: block; margin: auto;" aria-hidden="true">
              <path d="M160 16c0-8.8-7.2-16-16-16s-16 7.2-16 16V112H96c-17.7 0-32 14.3-32 32V272c0 17.7 14.3 32 32 32h224c17.7 0 32-14.3 32-32V144c0-17.7-14.3-32-32-32H256V16c0-8.8-7.2-16-16-16s-16 7.2-16 16V112H160V16zM64 336c0 53 43 96 96 96v48c0 17.7 14.3 32 32 32s32-14.3 32-32V432c53 0 96-43 96-96V320H64v16z"/>
            </svg>`
          : ''

        return `
        <div class="marker-container ${nearestClass} ${isOffline ? 'is-offline' : ''}">
          ${handHtml}
          <div class="marker-pin" style="background-color: ${backgroundColor};">
            <div class="availability-circle ${isAvailable ? 'available' : 'unavailable'}">
              ${plugSvg}
            </div>
            <span class="battery-count">${countText}</span>
          </div>
        </div>
      `
      },

      createClusterIcon(cluster) {
        const childMarkers = cluster.getAllChildMarkers()
        const childCount = childMarkers.length

        // Check if this cluster contains the nearest point
        const nearest = this.nearestPoint
        let containsNearest = false

        if (nearest && childCount > 0) {
          const nearestLat = parseFloat(nearest.geolocation.lat)
          const nearestLng = parseFloat(nearest.geolocation.lng)

          containsNearest = childMarkers.some(marker => {
            const markerLatLng = marker.getLatLng()
            // Allow a small margin for floating point comparison
            const latDiff = Math.abs(markerLatLng.lat - nearestLat)
            const lngDiff = Math.abs(markerLatLng.lng - nearestLng)
            return latDiff < 0.0001 && lngDiff < 0.0001
          })
        }

        const showHandOnCluster = this.showNearestHand && containsNearest && !this.selectedPoint
        const handText = this.isReturnMode ? this.$t('map_navigation.return_here') : this.$t('map_navigation.rent_here')

        const handHtml = showHandOnCluster
          ? `
        <div class="nearest-hand-indicator cluster-hand" data-cluster-hand="true">
          <div class="rent-here-balloon">${handText}</div>
        </div>
      `
          : ''

        return L.divIcon({
          html: `
          <div class="cluster-container ${showHandOnCluster ? 'has-hand' : ''}">
            ${handHtml}
            <div class="cluster-marker">
              <span class="cluster-count">${childCount}</span>
            </div>
          </div>
        `,
          className: 'custom-cluster',
          iconSize: [40, 40],
          iconAnchor: [20, 20]
        })
      },

      onMarkerClick(point) {
        if (point?.id || point?.point_id) {
          sessionStorage.setItem('point_id_token_selection', point.id || point.point_id)
        }

        this.trackEvent(
          SESSION_EVENTS.MAP_MARKER_CLICKED,
          this.getPointExtras(point, {
            has_availability: this.isReturnMode
              ? parseInt(point.available_slots) > 0
              : parseInt(point.available_batteries) > 0
          })
        )

        this.selectedPoint = point
        this.map.panTo([parseFloat(point.geolocation.lat), parseFloat(point.geolocation.lng)])
      },

      closeDialog() {
        if (this.selectedPoint && this.detailsOpenedAt) {
          const timeViewed = Math.floor((Date.now() - this.detailsOpenedAt) / 1000)
          this.trackEvent(SESSION_EVENTS.MAP_POINT_DETAILS_CLOSED, {
            point_id: this.selectedPoint.point_id,
            time_viewed_seconds: timeViewed
          })
        }

        this.selectedPoint = null
        this.detailsOpenedAt = null
      },

      handleDialogTokenChanged(newToken) {
        if (newToken && newToken.length === 4) {
          this.closeDialog()
          this.manualToken = newToken
          this.openManualTokenInput()
          this.validateManualToken()
        }
      },

      async handleSelectStation() {
        if (!this.isLoggedIn) {
          this.$router.push('/login-social')
          return
        }

        if (!this.selectedPoint) {
          return
        }

        if (this.distanceFromUser && parseFloat(this.distanceFromUser) > this.MAX_DISTANCE_KM) {
          this.trackEvent(SESSION_EVENTS.MAP_DISTANCE_WARNING_SHOWN, this.getPointExtras(this.selectedPoint))

          this.pendingWithdrawalData = {
            token: this.selectedPoint.token,
            pointName: this.selectedPoint.name,
            pointId: this.selectedPoint.point_id
          }
          this.showDistanceConfirmationModal = true
          return
        }

        await this.proceedWithSelection()
      },

      async proceedWithSelection() {
        // Allow retry even if already validating (we'll reset the flag)
        // if (this.isValidating) {
        //   return
        // }

        // If selectedPoint is null but we have pendingWithdrawalData, use that
        if (!this.selectedPoint && !this.pendingWithdrawalData) {
          console.error('[MapNavigation] proceedWithSelection called without selectedPoint or pendingWithdrawalData')
          return
        }

        const data = this.pendingWithdrawalData || {
          token: this.selectedPoint.token,
          pointName: this.selectedPoint.name,
          pointId: this.selectedPoint.point_id
        }

        // Save pendingWithdrawalData for potential retry
        this.pendingWithdrawalData = data

        this.isValidating = true

        localStorage.setItem('terminal_token', data.token)
        localStorage.setItem('point_name', data.pointName)
        localStorage.setItem('point_id', data.pointId.toString())

        // Only track if we have selectedPoint
        if (this.selectedPoint) {
          this.trackEvent(SESSION_EVENTS.MAP_POINT_SELECTED, this.getPointExtras(this.selectedPoint))
        }

        this.$emit('withdraw-battery', {
          token: data.token,
          pointName: data.pointName,
          pointId: data.pointId
        })
      },

      async confirmDistanceSelection() {
        this.showDistanceConfirmationModal = false

        this.trackEvent(SESSION_EVENTS.MAP_DISTANCE_WARNING_ACCEPTED, this.getPointExtras(this.selectedPoint))

        await this.proceedWithSelection()
      },

      cancelDistanceSelection() {
        this.showDistanceConfirmationModal = false

        this.trackEvent(SESSION_EVENTS.MAP_DISTANCE_WARNING_REJECTED, this.getPointExtras(this.selectedPoint))

        this.pendingWithdrawalData = null
      },

      handleCheckPrices() {
        if (this.selectedPoint) {
          this.trackEvent(SESSION_EVENTS.MAP_CHECK_PRICES_CLICKED, {
            point_id: this.selectedPoint.point_id,
            point_name: this.selectedPoint.name,
            token: this.selectedPoint.token
          })

          this.$emit('check-prices', {
            token: this.selectedPoint.token,
            pointName: this.selectedPoint.name,
            pointId: this.selectedPoint.point_id
          })
        }
      },

      handleStartCheckout(point) {
        // Open the internal checkout modal directly
        this.checkoutPoint = point
        this.showCheckoutModal = true

        this.trackEvent(SESSION_EVENTS.CHECKOUT_FLOW_OPENED, {
          point_id: point.point_id,
          point_name: point.name,
          source: 'point_details'
        })
      },

      closeCheckoutModal() {
        this.showCheckoutModal = false
        this.checkoutPoint = null
        this.pendingPixData = null
        this.isValidating = false

        // Reset manual input loading state if it exists
        if (this.$refs.manualTokenInputDialogRef) {
          this.$refs.manualTokenInputDialogRef.resetLoading()
        }
      },

      handleStartRental(transactionData) {
        // Emit to parent (home-page)
        this.$emit('start-rental', transactionData)

        // Close all modals and cleanup
        this.closeCheckoutModal()
        this.closeManualInput()
        this.selectedPoint = null
        this.pendingWithdrawalData = null
        this.isValidating = false
      },

      closeManualInput() {
        // Completely close manual token input dialog
        this.showManualTokenInput = false
        this.isManualInputMinimized = false
        this.manualToken = ''
        this.manualSelectedPoint = null
        this.tokenInputError = ''
      },

      handleCableChanged(cableType) {
        // Emit cable type change to parent
        this.$emit('change-cable-type', cableType)
      },

      async handleValidateTransaction(validationData) {
        // Save for retry mechanisms (like handleSelectCable from error dialog)
        console.log('[MapNavigation] handleValidateTransaction saving pending data:', validationData)
        this.pendingWithdrawalData = {
          token: validationData.token,
          pointName: validationData.pointName,
          pointId: validationData.pointId,
          cableType: validationData.cableType,
          updatedUser: validationData.updatedUser,
          paymentMethod: validationData.paymentMethod
        }

        // This will delegate validation to the parent (home-page.js)
        // which has the actual validation logic in handleMapWithdrawBattery
        this.$emit('withdraw-battery', this.pendingWithdrawalData)
      },

      async handleAllowLocation() {
        this.showLocationModal = false
        this.gpsStatus = 'waiting'
        this.geolocationAttempts++

        this.trackEvent(SESSION_EVENTS.MAP_LOCATION_PERMISSION_REQUESTED, {
          is_retry: this.geolocationAttempts > 1
        })

        try {
          // Make sure points are fetched
          if (this.points.length === 0) {
            await this.fetchPoints()
          }

          // Set default center immediately
          this.userLocation = calculateMapCenterFromPoints(this.points, this.mode)
          if (!this.userLocation || !this.userLocation.lat) {
            this.userLocation = DEFAULT_CENTER
          }

          // Initialize map with default center
          await this.initializeMapContent()

          // Then start background GPS acquisition
          const permissionResult = await requestGeolocationPermission()

          if (permissionResult.blocked) {
            this.gpsStatus = 'failed'
            this.showPermissionBlockedModal = true

            this.trackEvent(SESSION_EVENTS.MAP_LOCATION_PERMISSION_DENIED, {
              geolocation_attempts: this.geolocationAttempts
            })
            return
          }

          if (permissionResult.granted) {
            this.trackEvent(SESSION_EVENTS.MAP_LOCATION_PERMISSION_GRANTED, {
              geolocation_attempts: this.geolocationAttempts
            })

            // Start background GPS acquisition
            this.startBackgroundGPSAcquisition()
          } else {
            // Permission still not granted
            console.warn('Geolocation permission denied by user')
            this.gpsStatus = 'failed'

            if (this.geolocationAttempts >= this.maxGeolocationAttempts && !this.hideManualInput) {
              this.showManualTokenInput = true
            }
          }
        } catch (error) {
          console.error('Error in handleAllowLocation:', error)
          this.gpsStatus = 'failed'

          if (this.geolocationAttempts >= this.maxGeolocationAttempts) {
            if (!this.hideManualInput) {
              this.showManualTokenInput = true
            }
          } else if (!this.map) {
            // If map wasn't initialized, initialize it now
            await this.initializeMapContent()
          }
        }
      },

      handlePermissionBlockedOk() {
        this.showPermissionBlockedModal = false
        this.gpsStatus = 'failed'

        this.trackEvent(SESSION_EVENTS.MAP_MANUAL_INPUT_OPENED, {
          reason: 'permission_blocked'
        })

        this.fetchPoints()
          .then(async () => {
            this.userLocation = calculateMapCenterFromPoints(this.points, this.mode)
            if (!this.userLocation || !this.userLocation.lat) {
              this.userLocation = DEFAULT_CENTER
            }

            await this.initializeMapContent()
            if (!this.hideManualInput) {
              this.showManualTokenInput = true
            }
          })
          .catch(error => {
            console.error('Error fetching points:', error)
            this.userLocation = DEFAULT_CENTER
            if (!this.hideManualInput) {
              this.showManualTokenInput = true
            }
          })
      },

      async handleSkipLocation() {
        this.showLocationModal = false
        this.gpsStatus = 'failed'

        this.trackEvent(SESSION_EVENTS.MAP_LOCATION_PERMISSION_SKIPPED)

        this.trackEvent(SESSION_EVENTS.MAP_MANUAL_INPUT_OPENED, {
          reason: 'skip_permission'
        })

        try {
          if (this.points.length === 0) {
            await this.fetchPoints()
          }

          this.userLocation = calculateMapCenterFromPoints(this.points, this.mode)
          if (!this.userLocation || !this.userLocation.lat) {
            this.userLocation = DEFAULT_CENTER
          }

          // Initialize map
          await this.initializeMapContent()
        } catch (error) {
          console.error('Error fetching points:', error)
          this.userLocation = DEFAULT_CENTER
        }

        if (!this.hideManualInput) {
          this.showManualTokenInput = true
        }
        localStorage.setItem('skip_location', 'true')
      },

      hasLocationSkiped() {
        return localStorage.getItem('skip_location') === 'true'
      },

      async initializeMapContent() {
        try {
          // Guard: if map is already initialized, just refresh markers
          if (this.map) {
            this.displayMarkers()
            return
          }

          if (this.points.length === 0) {
            await this.fetchPoints()
          }

          // Initialize map with current userLocation or default
          const mapCenter = this.userLocation || DEFAULT_CENTER
          this.map = L.map(this.$refs.mapContainer, {
            attributionControl: false,
            zoomControl: true,
            minZoom: 6
          }).setView([mapCenter.lat, mapCenter.lng], 15)

          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            subdomains: 'abc',
            maxZoom: 19
          }).addTo(this.map)

          this.markerClusterGroup = L.markerClusterGroup({
            maxClusterRadius: 80,
            disableClusteringAtZoom: 16,
            showCoverageOnHover: true,
            zoomToBoundsOnClick: true,
            iconCreateFunction: cluster => {
              return this.createClusterIcon(cluster)
            }
          })

          // Handle cluster clicks (only for the "Rent here" balloon)
          this.markerClusterGroup.on('clusterclick', e => {
            // If the click was on the "Rent here" balloon, open the nearest point details
            if (e.originalEvent && e.originalEvent.target && e.originalEvent.target.closest('.rent-here-balloon')) {
              if (this.nearestPoint) {
                // Stop propagation to prevent Leaflet from zooming automatically if we click the balloon
                if (e.originalEvent) {
                  L.DomEvent.stopPropagation(e.originalEvent)
                }
                this.onMarkerClick(this.nearestPoint)
              }
            }
          })

          this.map.addLayer(this.markerClusterGroup)

          // Add user location marker (always add if we have a location)
          if (this.userLocation && this.userLocation.lat && this.userLocation.lng) {
            this.updateUserMarker(this.userLocation)
          } else {
            console.warn('[MapNavigation] No valid user location for marker:', this.userLocation)
          }

          if (!this.hasLocationPermission && this.gpsStatus === 'failed' && !this.hideManualInput) {
            this.openManualTokenInput()
          }

          this.displayMarkers()
          this.loading = false

          // Listen to zoom changes to update hand visibility only;
          // Leaflet MarkerCluster handles clustering/unclustering automatically on zoom,
          // so calling displayMarkers() here would destroy expanded cluster state.
          this.map.on('zoomend', () => {
            this.updateHandVisibility()
          })

          // When user drags the map, deactivate the "centered" state on the locate button
          this.map.on('dragstart', () => {
            this.isMapCentered = false
          })

          // Track successful map initialization
          this.trackEvent(SESSION_EVENTS.MAP_INITIALIZED, {
            has_location_permission: this.hasLocationPermission,
            points_count: this.points.length,
            gps_status: this.gpsStatus
          })

          // Hide hand indicator after 30 seconds
          setTimeout(() => {
            this.showNearestHand = false
            // Just update hand visibility without reconstructing the entire marker layer
            this.updateHandVisibility()
          }, 30000)
        } catch (error) {
          this.loading = false
          this.loadingError = true
          console.error('Error initializing map content:', error)
        }
      },

      onManualTokenInput(value) {
        this.manualToken = value
        this.tokenInputError = ''

        if (this.manualToken.length === 4) {
          this.validateManualToken()
        } else {
          this.manualSelectedPoint = null
        }
      },

      async validateManualToken() {
        if (this.manualToken.length !== 4) {
          this.tokenInputError = this.$t('map_navigation.token_invalid_length')
          this.manualSelectedPoint = null
          return
        }

        try {
          // Buscar o ponto via API usando o token - sempre obter dados frescos da API
          const pointData = await getPointByToken(this.manualToken)

          if (!pointData || !pointData.id) {
            this.tokenInputError = this.$t('map_navigation.token_not_found')
            this.manualSelectedPoint = null

            // Track invalid token
            this.trackEvent(SESSION_EVENTS.MAP_MANUAL_TOKEN_INVALID, {
              token: this.manualToken,
              error_type: 'token_not_found'
            })
            return
          }

          this.manualSelectedPoint = {
            point_id: pointData.id,
            name: pointData.name,
            address: pointData.address || this.$t('map_navigation.address_not_available'),
            available_batteries: pointData.available_batteries || pointData.avaible_batteries || 0,
            available_slots: pointData.available_slots || pointData.avaible_slots || 0,
            geolocation: pointData.geolocation || null,
            token: this.manualToken
          }

          const hasAvailability = this.isReturnMode
            ? parseInt(this.manualSelectedPoint.available_slots) > 0
            : parseInt(this.manualSelectedPoint.available_batteries) > 0

          if (!hasAvailability) {
            this.tokenInputError = this.isReturnMode
              ? this.$t('map_navigation.no_slots')
              : this.$t('map_navigation.no_batteries_available')

            // Track no availability
            this.trackEvent(SESSION_EVENTS.MAP_MANUAL_TOKEN_INVALID, {
              token: this.manualToken,
              error_type: this.isReturnMode ? 'no_slots_available' : 'no_batteries_available',
              point_id: this.manualSelectedPoint.point_id,
              point_name: this.manualSelectedPoint.name
            })
          } else {
            if (document.activeElement && document.activeElement.blur) {
              document.activeElement.blur()
            }

            this.tokenInputError = ''

            // Track successful validation
            this.trackEvent(SESSION_EVENTS.MAP_MANUAL_TOKEN_VALIDATED, {
              token: this.manualToken,
              point_id: this.manualSelectedPoint.point_id,
              point_name: this.manualSelectedPoint.name,
              available_batteries: this.manualSelectedPoint.available_batteries,
              available_slots: this.manualSelectedPoint.available_slots
            })
          }
        } catch (error) {
          console.error('Error validating token:', error)
          this.tokenInputError = this.$t('map_navigation.token_not_found')
          this.manualSelectedPoint = null

          // Track validation error
          this.trackEvent(SESSION_EVENTS.MAP_MANUAL_TOKEN_INVALID, {
            token: this.manualToken,
            error_type: 'validation_error',
            error_message: error.message
          })
        }
      },

      handleSelectManualStation() {
        if (!this.isLoggedIn) {
          this.$router.push('/login-social')
          return
        }

        if (this.manualSelectedPoint && this.manualToken) {
          sessionStorage.setItem(
            'point_id_token_selection',
            this.manualSelectedPoint.id || this.manualSelectedPoint.point_id
          )

          // Open the checkout modal with the manual point
          this.checkoutPoint = {
            ...this.manualSelectedPoint,
            token: this.manualToken
          }
          this.showCheckoutModal = true

          // We no longer minimize the manual input here, so that if the checkout is cancelled,
          // the dialog is still visible to the user as they left it.

          localStorage.setItem('terminal_token', this.manualToken)
          localStorage.setItem('point_name', this.manualSelectedPoint.name)
          localStorage.setItem('point_id', this.manualSelectedPoint.point_id.toString())

          // Track manual station selection
          this.trackEvent(SESSION_EVENTS.MAP_POINT_SELECTED, {
            token: this.manualToken,
            point_id: this.manualSelectedPoint.point_id,
            point_name: this.manualSelectedPoint.name
          })
        }
      },

      handleCheckManualPrices() {
        if (this.manualSelectedPoint && this.manualToken) {
          this.$emit('check-prices', {
            token: this.manualToken,
            pointName: this.manualSelectedPoint.name,
            pointId: this.manualSelectedPoint.point_id
          })
        }
      },

      toggleMinimizeManualInput() {
        this.isManualInputMinimized = !this.isManualInputMinimized

        // Track minimize/maximize action
        this.trackEvent(
          this.isManualInputMinimized
            ? SESSION_EVENTS.MAP_MANUAL_INPUT_MINIMIZED
            : SESSION_EVENTS.MAP_MANUAL_INPUT_OPENED
        )
      },

      handleManualInputPillClick() {
        // User clicked the manual input pill — clear state and open fresh
        this.manualToken = ''
        this.manualSelectedPoint = null
        this.tokenInputError = ''
        this.openManualTokenInput()
      },

      openManualTokenInput() {
        // Open manual token input (maximized)
        this.showManualTokenInput = true
        this.isManualInputMinimized = false
        if (this.hasLocationPermission) {
          this.showPermissionBlockedModal = false
        }

        this.trackEvent(SESSION_EVENTS.MAP_MANUAL_INPUT_OPENED, {
          reason: this.manualToken ? 'programmatic' : 'user_action'
        })
      },

      minimizeManualInput() {
        // Just minimize, don't close
        this.isManualInputMinimized = true

        // Track minimize action
        this.trackEvent(SESSION_EVENTS.MAP_MANUAL_INPUT_MINIMIZED)
      },

      async retryGeolocation() {
        // Hide manual input and show location modal again
        this.geolocationAttempts = 0 // Reset attempts
        this.showLocationModal = true

        // Track retry action
        this.trackEvent(SESSION_EVENTS.MAP_LOCATION_RETRY, {
          retry_count: this.geolocationAttempts
        })
      },

      async processInitialToken() {
        try {
          // Wait for map to be fully initialized
          if (!this.map || this.points.length === 0) {
            // Wait a bit for the map to be ready
            await new Promise(resolve => setTimeout(resolve, 500))
          }

          // Sempre buscar dados frescos da API em vez de confiar no cache local
          const pointData = await getPointByToken(this.initialToken)

          // Usar os dados frescos da API incluindo geolocalização
          this.selectedPoint = {
            point_id: pointData.id,
            name: pointData.name,
            address: pointData.address || this.$t('map_navigation.address_not_available'),
            available_batteries: pointData.available_batteries || 0,
            available_slots: pointData.available_slots || 0,
            geolocation: pointData.geolocation || null,
            token: this.initialToken
          }

          // Pan to the point location
          if (this.selectedPoint && this.selectedPoint.geolocation) {
            const lat = parseFloat(this.selectedPoint.geolocation.lat)
            const lng = parseFloat(this.selectedPoint.geolocation.lng)

            this.map.setView([lat, lng], 16, {
              animate: true,
              duration: 1
            })

            this.trackEvent(SESSION_EVENTS.MAP_QR_POINT_OPENED, {
              token: this.initialToken,
              point_id: this.selectedPoint.point_id,
              point_name: this.selectedPoint.name
            })

            // Tenta encontrar o ponto na lista local para garantir consistência
            const point = this.points.find(p => p.point_id === this.selectedPoint.point_id) || this.selectedPoint

            // Abre automaticamente os detalhes do ponto (simulando o clique)
            this.onMarkerClick(point)
          }
        } catch (error) {
          console.error('[MapNavigation] Error processing initial token:', error)
        }
      },

      showValidationError(errorData) {
        this.validationError = errorData
        this.showValidationErrorDialog = true

        this.trackEvent(SESSION_EVENTS.MAP_VALIDATION_ERROR, {
          error_code: errorData.code,
          error_message: errorData.error,
          transaction_id: errorData.transaction_id
        })
      },

      closeValidationErrorDialog() {
        this.showValidationErrorDialog = false
        this.validationError = null
      },

      handleValidationErrorClose() {
        this.closeValidationErrorDialog()
        if (this.showCheckoutModal) {
          this.closeCheckoutModal()
        }
      },

      handleSecondaryErrorAction(payload = {}) {
        const errorType =
          payload.errorType || (this.validationError?.code ? getErrorType(this.validationError.code) : 'generic')

        console.log('[MapNavigation] handleSecondaryErrorAction for type:', errorType)

        this.closeValidationErrorDialog()

        if (errorType === ERROR_TYPES.TERMINAL_OFFLINE) {
          // Terminal offline: apenas volta ao mapa sem abrir input manual
          this.closeCheckoutModal()
          return
        }

        if (errorType === ERROR_TYPES.TRANSACTION_EXPIRED) {
          // Transação expirada: volta e abre input manual para nova tentativa
          this.closeCheckoutModal()
          this.openManualTokenInput()
          return
        }

        if (errorType === ERROR_TYPES.NO_BATTERY_WITH_CABLE) {
          // Volta para seleção de conector
          if (this.$refs.checkoutFlowModalRef?.openCableSelector) {
            this.$refs.checkoutFlowModalRef.openCableSelector()
          }
          return
        }

        if (errorType === ERROR_TYPES.PAYMENT_ERROR) {
          // Abre seletor de pagamento
          if (this.$refs.checkoutFlowModalRef?.openPaymentSelector) {
            this.$refs.checkoutFlowModalRef.openPaymentSelector()
          }
        }
      },

      handleChangeCableType() {
        this.closeValidationErrorDialog()
        // Emit event to parent to handle cable type change
        this.$emit('change-cable-type')
      },

      handleSelectCable(cableType) {
        // DO NOT close the dialog - keep it open to show retry progress
        // this.closeValidationErrorDialog()

        console.log('[MapNavigation] handleSelectCable called with:', cableType)
        console.log('[MapNavigation] Current state:', {
          pendingWithdrawalData: this.pendingWithdrawalData,
          manualSelectedPoint: this.manualSelectedPoint,
          isValidating: this.isValidating
        })

        // Track cable selection from error dialog
        this.trackEvent(SESSION_EVENTS.MAP_CABLE_SELECTED_FROM_ERROR, {
          error_code: this.validationError?.code,
          selected_cable_type: cableType,
          previous_cable_type: this.cableType
        })

        // Emit event to parent to update cable type (this might not trigger watcher if same value)
        this.$emit('change-cable-type', cableType)

        // ALWAYS retry validation after a small delay to ensure parent updated
        // This handles the case where user selects the same cable type
        this.$nextTick(() => {
          setTimeout(() => {
            console.log('[MapNavigation] Retrying validation with selected cable type:', cableType)
            console.log('[MapNavigation] Retry state check:', {
              pendingWithdrawalData: this.pendingWithdrawalData,
              manualSelectedPoint: this.manualSelectedPoint
            })

            // Call proceedWithSelection directly instead of handleSelectStation
            // because we already have pendingWithdrawalData and don't need to check distance again
            if (this.pendingWithdrawalData) {
              console.log('[MapNavigation] Retrying with pendingWithdrawalData - calling proceedWithSelection')

              // Temporarily restore selectedPoint from pendingWithdrawalData if needed
              // proceedWithSelection needs selectedPoint for tracking
              if (!this.selectedPoint) {
                const point = this.points.find(p => p.point_id === this.pendingWithdrawalData.pointId)
                if (point) {
                  this.selectedPoint = point
                  console.log('[MapNavigation] Restored selectedPoint from points array')
                }
              }

              this.proceedWithSelection()
            } else if (this.manualSelectedPoint) {
              console.log('[MapNavigation] Retrying with manualSelectedPoint')
              this.handleSelectManualStation()
            } else {
              console.error('[MapNavigation] ERROR: No pending data to retry validation!')
              console.error('[MapNavigation] This should not happen - data may have been cleared prematurely')
            }
          }, 300)
        })
      },

      handleRetryValidation() {
        // Close the error dialog
        this.closeValidationErrorDialog()

        // Track retry action
        this.trackEvent(SESSION_EVENTS.MAP_VALIDATION_RETRY, {
          error_code: this.validationError?.code
        })

        // Retry the validation
        if (this.$refs.checkoutFlowModalRef && this.showCheckoutModal) {
          // If the checkout modal is open, let it handle the validation (it shows the spinner)
          console.log('[MapNavigation] Retrying via CheckoutFlowModal')
          this.$refs.checkoutFlowModalRef.validateTransaction()
        } else if (this.pendingWithdrawalData) {
          this.handleSelectStation()
        } else if (this.manualSelectedPoint) {
          this.handleSelectManualStation()
        }
      },

      handleSelectNearbyTerminal(terminal) {
        // Close the error dialog
        this.closeValidationErrorDialog()

        // Track nearby terminal selection
        this.trackEvent(SESSION_EVENTS.MAP_NEARBY_TERMINAL_SELECTED, {
          from_error_code: this.validationError?.code,
          terminal_id: terminal.point_id,
          terminal_name: terminal.name,
          distance_meters: terminal.distanceMeters
        })

        // Find the point in the points array to get full data
        const point = this.points.find(p => p.point_id === terminal.point_id)
        if (point) {
          // Open the point details dialog
          this.selectedPoint = point
        }
      },

      stopValidating() {
        this.isValidating = false
        // Reset manual input state after successful selection
        this.manualToken = ''
        this.manualSelectedPoint = null
        this.tokenInputError = ''
        // Clear dialog state - closes the dialog
        this.selectedPoint = null
        this.pendingWithdrawalData = null
      },

      showError(errorData) {
        this.isValidating = false
        this.showValidationError(errorData)
      },

      notifyValidationSuccess(transactionData) {
        console.log('[MapNavigation] notifyValidationSuccess called', transactionData)
        this.isValidating = false

        // Notify CheckoutFlowModal if it's open
        if (this.$refs.checkoutFlowModalRef) {
          if (this.showCheckoutModal) {
            console.log('[MapNavigation] Calling CheckoutFlowModal.onValidationSuccess')
            this.$refs.checkoutFlowModalRef.onValidationSuccess(transactionData)
          }
        } else {
          console.error('[MapNavigation] checkoutFlowModalRef is missing')
        }
      },

      notifyValidationError(errorData) {
        this.isValidating = false

        // Do NOT clear pendingWithdrawalData or manualSelectedPoint here
        // We need them for retry when user clicks "Try Again" or selects a cable

        let handled = false
        // Notify CheckoutFlowModal if it's open
        if (this.$refs.checkoutFlowModalRef && this.showCheckoutModal) {
          handled = this.$refs.checkoutFlowModalRef.onValidationError(errorData)
        }

        // Also show the error dialog for user to handle if NOT handled by modal
        if (!handled) {
          this.showValidationError(errorData)
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  .map-navigation-container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: var(--app-fixed-bottom-offset, 0px);
    width: 100%;
    height: auto;
    min-height: var(--app-viewport-safe-height, 100vh);
    min-height: var(--app-viewport-safe-height, 100dvh);
    background: #fff;
    flex-shrink: 0;
    z-index: 1;
  }

  .map-container {
    width: 100%;
    height: 100%;
    overflow: hidden;

    :deep(.leaflet-tile-container) {
      filter: grayscale(1) contrast(0.95);
    }
  }

  .map-back-btn {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 1100;
    background: #fff;
    border: none;
    border-radius: 20px;
    padding: 8px 14px 8px 10px;
    height: 36px;
    display: flex;
    align-items: center;
    gap: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    cursor: pointer;
    font-size: 16px;
    color: #424242;
    transition: all 0.3s ease;

    .back-text {
      font-size: 13px;
      font-weight: 600;
      line-height: 1;
      white-space: nowrap;
    }

    &:hover {
      background: #f5f5f5;
      transform: scale(1.05);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  .map-close-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 1100;
    background: #fff;
    border: none;
    border-radius: 50%;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    cursor: pointer;
    font-size: 24px;
    color: #424242;
    transition: all 0.3s ease;

    &:hover {
      background: #f5f5f5;
      transform: scale(1.05);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  // Map Overlay for Manual Input
  .map-overlay-backdrop {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(3px);
    z-index: 1001;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  // Floating Location Retry Button
  .location-retry-floating-btn {
    position: fixed;
    top: calc(var(--app-fixed-top-offset, 0px) + (var(--app-viewport-safe-height, 100vh) / 2));
    top: calc(var(--app-fixed-top-offset, 0px) + (var(--app-viewport-safe-height, 100dvh) / 2));
    left: 50%;
    transform: translate(-50%, -50%);
    background: linear-gradient(135deg, #27ae60 0%, #229954 100%);
    color: #fff;
    border: none;
    border-radius: 50px;
    padding: 16px 32px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 8px 24px rgba(39, 174, 96, 0.4);
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 12px;
    z-index: 1002;
    animation: pulse-glow 2s infinite;

    @keyframes pulse-glow {
      0%,
      100% {
        box-shadow: 0 8px 24px rgba(39, 174, 96, 0.4);
      }
      50% {
        box-shadow: 0 8px 32px rgba(39, 174, 96, 0.6);
      }
    }

    .location-icon {
      font-size: 20px;
      animation: spin-pulse 3s ease-in-out infinite;

      @keyframes spin-pulse {
        0%,
        100% {
          transform: scale(1);
        }
        25% {
          transform: scale(1.1) rotate(90deg);
        }
        50% {
          transform: scale(1);
        }
        75% {
          transform: scale(1.1) rotate(-90deg);
        }
      }
    }

    .retry-text {
      font-size: 15px;
    }

    &:hover {
      transform: translate(-50%, -50%) translateY(-3px);
      box-shadow: 0 12px 32px rgba(39, 174, 96, 0.5);
    }

    &:active {
      transform: translate(-50%, -50%) translateY(-1px);
    }

    @media (max-width: 768px) {
      padding: 14px 28px;
      font-size: 14px;

      .location-icon {
        font-size: 18px;
      }

      .retry-text {
        font-size: 14px;
      }
    }

    @media (max-width: 480px) {
      padding: 12px 24px;
      font-size: 13px;

      .location-icon {
        font-size: 16px;
      }

      .retry-text {
        font-size: 13px;
      }
    }
  }

  // Custom marker styles
  :deep(.custom-marker) {
    background: transparent;
    border: none;
  }

  :deep(.marker-container) {
    position: relative;
    width: 40px;
    height: 50px;
  }

  :deep(.marker-pin) {
    width: 40px;
    height: 40px;
    border-radius: 50% 50% 50% 0;
    background: #0066cc;
    position: absolute;
    transform: rotate(-45deg);
    left: 50%;
    top: 50%;
    margin: -20px 0 0 -20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }

  :deep(.is-nearest) {
    .marker-pin {
      box-shadow:
        0 0 20px rgba(0, 102, 204, 0.8),
        0 2px 8px rgba(0, 0, 0, 0.3);
      z-index: 11;
    }
  }

  :deep(.nearest-hand-indicator) {
    position: absolute;
    top: -60px;
    left: 50%;
    transform: translateX(-50%);
    filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.2));
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  :deep(.rent-here-balloon) {
    background-color: #ffffff;
    color: #001e38;
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    letter-spacing: 0px;
    position: relative;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 1.5px solid #001e38;
    cursor: pointer;
  }

  :deep(.rent-here-balloon::before) {
    content: '';
    position: absolute;
    bottom: -9px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 9px solid transparent;
    border-right: 9px solid transparent;
    border-top: 9px solid #001e38;
  }

  :deep(.rent-here-balloon::after) {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid #ffffff;
  }

  :deep(.availability-circle) {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    position: relative;
    z-index: 2;
    transform: rotate(45deg);
    display: flex;
    align-items: center;
    justify-content: center;

    &.available {
      box-shadow:
        inset 0 0 0 4px rgba(40, 167, 69, 0.8),
        0 0 0 2px rgba(40, 167, 69, 0.6);
    }

    &.unavailable {
      box-shadow:
        inset 0 0 0 4px rgba(220, 53, 69, 0.8),
        0 0 0 2px rgba(220, 53, 69, 0.6);
    }
  }

  :deep(.battery-count) {
    position: absolute;
    bottom: 0px;
    top: 10px;
    right: 10px;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #000;
    font-weight: bold;
    font-size: 10px;
    z-index: 3;
    transform: rotate(45deg);
  }

  :deep(.is-offline) {
    .marker-pin {
      opacity: 0.9;
    }
  }

  :deep(.user-location-marker) {
    background: transparent;
    border: none;
    z-index: 401 !important;
  }

  .active-rental-overlay {
    position: absolute;
    top: var(--app-fixed-top-offset, 0px);
    left: 0;
    right: 0;
    bottom: var(--app-fixed-bottom-offset, 0px);
    background: #f6f7f9;
    z-index: 9999; // Keep below SideBar (1600)
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .active-rental-content {
    padding: 20px;
    padding-top: 80px; // Space for the floating button
    max-width: 600px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .view-info-btn {
    background: #1976d2;
    color: white;
    border: none;
  }

  :deep(.user-marker-pulse) {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #0066cc;
    border: 3px solid #fff;
    box-shadow: 0 0 0 0 rgba(0, 102, 204, 0.7);
    animation: pulse 2s infinite;
    z-index: 401 !important;
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(0, 102, 204, 0.7);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(0, 102, 204, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(0, 102, 204, 0);
    }
  }

  :deep(.marker-cluster) {
    background: transparent;
    border: none;
    box-shadow: none;
  }

  :deep(.marker-cluster div) {
    background: linear-gradient(135deg, #0066cc 0%, #0052a3 100%);
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #fff;
    font-weight: bold;
    font-size: 14px;
    font-family: 'Segoe UI', sans-serif;
    border: 3px solid #fff;
    box-shadow:
      0 4px 12px rgba(0, 102, 204, 0.35),
      inset 0 -2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
  }

  :deep(.marker-cluster:hover div) {
    background: linear-gradient(135deg, #0052a3 0%, #003d7a 100%);
    box-shadow:
      0 6px 16px rgba(0, 102, 204, 0.45),
      inset 0 -2px 4px rgba(0, 0, 0, 0.15);
    transform: scale(1.1);
  }

  @keyframes cluster-pop {
    0% {
      transform: scale(0.8);
      opacity: 0;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  :deep(.custom-cluster) {
    background: transparent;
    border: none;
  }

  :deep(.cluster-container) {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.cluster-container .cluster-hand) {
    position: absolute;
    top: -45px;
    left: 50%;
    z-index: 10;
    animation: point-down 0.6s infinite alternate ease-in-out;
    cursor: pointer;
  }

  :deep(.cluster-marker) {
    background: linear-gradient(135deg, #0066cc 0%, #0052a3 100%);
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #fff;
    font-weight: bold;
    font-size: 14px;
    font-family: 'Segoe UI', sans-serif;
    border: 3px solid #fff;
    box-shadow:
      0 4px 12px rgba(0, 102, 204, 0.35),
      inset 0 -2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
  }

  :deep(.cluster-container:hover .cluster-marker) {
    background: linear-gradient(135deg, #0052a3 0%, #003d7a 100%);
    box-shadow:
      0 6px 16px rgba(0, 102, 204, 0.45),
      inset 0 -2px 4px rgba(0, 0, 0, 0.15);
    transform: scale(1.1);
  }

  :deep(.cluster-count) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  @media (max-width: 768px) {
    .map-close-btn {
      top: 12px;
      right: 12px;
      width: 40px;
      height: 40px;
      font-size: 20px;
    }
  }

  // Leaflet zoom controls - just move slightly below sidebar
  :deep(.leaflet-control-zoom) {
    top: 80px !important;
    left: 10px !important;
  }

  // GPS Status Widget
  .gps-status-widget {
    position: fixed;
    top: calc(var(--app-fixed-top-offset, 0px) + (var(--app-viewport-safe-height, 100vh) / 2));
    top: calc(var(--app-fixed-top-offset, 0px) + (var(--app-viewport-safe-height, 100dvh) / 2));
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 9999;
    pointer-events: none;

    .widget-content {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 20px;
      border-radius: 24px;
      background: white;
      box-shadow:
        0 8px 24px rgba(0, 0, 0, 0.2),
        0 2px 4px rgba(0, 0, 0, 0.1);
      font-weight: 700;
      font-size: 14px;
      white-space: nowrap;
      border: 1px solid rgba(0, 0, 0, 0.05);

      .status-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 20px;

        svg {
          font-size: 18px;

          &.spinning {
            animation: spin 1s linear infinite;
          }
        }
      }

      .status-text {
        color: #333;
      }

      &.status-waiting {
        border-left: 4px solid #1976d2;

        .status-icon {
          color: #1976d2;
        }
      }

      &.status-acquired {
        border-left: 4px solid #2e7d32;

        .status-icon {
          color: #2e7d32;
        }
        .status-text {
          color: #2e7d32;
        }
      }
    }

    @media (max-width: 480px) {
      width: 90%;
      max-width: 300px;
      .widget-content {
        justify-content: center;
        padding: 14px;
      }
    }
  }

  // Refresh Points Button
  .refresh-points-btn {
    position: absolute;
    bottom: calc(170px + var(--app-fixed-bottom-offset, 170px));
    right: 16px;
    z-index: 999;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: none;
    background: #fff;
    box-shadow:
      0 2px 10px rgba(0, 0, 0, 0.25),
      0 1px 3px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition:
      box-shadow 0.2s ease,
      transform 0.15s ease,
      background 0.2s ease;
    -webkit-tap-highlight-color: transparent;

    &:active {
      transform: scale(0.92);
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
    }

    &:hover {
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);
    }

    .refresh-icon {
      font-size: 17px;
      color: #1976d2;

      &.spinning {
        animation: spin 1s linear infinite;
      }
    }
  }

  // Locate Me Button
  .locate-me-btn {
    position: absolute;
    bottom: calc(110px + var(--app-fixed-bottom-offset, 110px));
    right: 16px;
    z-index: 999;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: none;
    background: #fff;
    box-shadow:
      0 2px 10px rgba(0, 0, 0, 0.25),
      0 1px 3px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition:
      box-shadow 0.2s ease,
      transform 0.15s ease,
      background 0.2s ease;
    -webkit-tap-highlight-color: transparent;

    &:active {
      transform: scale(0.92);
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
    }

    &:hover {
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);
    }

    &.state-idle .locate-icon {
      font-size: 17px;
      color: #1976d2;
    }

    &.state-locating {
      .locate-spinner {
        width: 17px;
        height: 17px;
        border: 2px solid rgba(25, 118, 210, 0.25);
        border-top-color: #1976d2;
        border-radius: 50%;
        animation: locate-spin 0.8s linear infinite;
      }
    }

    &.state-centered {
      background: #1976d2;
      box-shadow: 0 2px 10px rgba(25, 118, 210, 0.45);

      .locate-icon {
        font-size: 17px;
        color: #fff;
      }
    }

    &.state-denied .locate-icon {
      font-size: 17px;
      color: #bdbdbd;
    }

    &.flash-acquired {
      background: #43a047;
      box-shadow: 0 0 0 0 rgba(67, 160, 71, 0.5);
      animation: locate-pulse 0.6s ease-in-out 2;

      .locate-icon {
        font-size: 17px;
        color: #fff;
      }
    }
  }

  // Manual Input Pill (Glassmorphism Style)
  .manual-input-pill {
    position: absolute;
    bottom: calc(24px + var(--app-fixed-bottom-offset, 24px));
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 12px 22px;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 50px;
    color: #1976d2;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    box-shadow:
      0 4px 16px rgba(0, 0, 0, 0.1),
      0 2px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 1000;
    white-space: nowrap;
    letter-spacing: 0.3px;
    -webkit-tap-highlight-color: transparent;

    .pill-icon {
      font-size: 16px;
      opacity: 0.9;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.95);
      transform: translateX(-50%) translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
      color: #1565c0;
    }

    &:active {
      transform: translateX(-50%) translateY(0) scale(0.96);
      background: rgba(255, 255, 255, 1);
    }

    @media (max-width: 768px) {
      padding: 10px 18px;
      font-size: 13px;
      bottom: calc(20px + var(--app-fixed-bottom-offset, 20px));

      .pill-icon {
        font-size: 14px;
      }
    }

    @media (max-width: 480px) {
      padding: 8px 16px;
      font-size: 12px;
      bottom: calc(16px + var(--app-fixed-bottom-offset, 16px));
    }
  }

  // Entrance Animation
  .pill-entrance-enter-active {
    transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }

  .pill-entrance-enter-from {
    opacity: 0;
    transform: translateX(-50%) translateY(20px) scale(0.8);
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes locate-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes locate-pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(67, 160, 71, 0.6);
    }
    50% {
      box-shadow: 0 0 0 12px rgba(67, 160, 71, 0);
      transform: scale(1.08);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(67, 160, 71, 0);
      transform: scale(1);
    }
  }

  // Toast enter/leave transitions
  .gps-toast-enter-active {
    animation: toast-in 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }
  .gps-toast-leave-active {
    animation: toast-out 0.4s cubic-bezier(0.6, -0.28, 0.735, 0.045) forwards;
  }

  @keyframes toast-in {
    from {
      opacity: 0;
      transform: translate(-50%, -80%);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%);
    }
  }

  @keyframes toast-out {
    from {
      opacity: 1;
      transform: translate(-50%, -50%);
    }
    to {
      opacity: 0;
      transform: translate(-50%, -20%);
    }
  }

  .slide-up-enter-active,
  .slide-up-leave-active {
    transition:
      transform 0.3s ease,
      opacity 0.3s ease;
  }

  .slide-up-enter-from,
  .slide-up-leave-to {
    transform: translateY(100%);
    opacity: 0;
  }
</style>
